import { type OrderbookRowItem } from "~/features/orderbook/models/orderbookModels";
import * as ApiHelper from "~/common/utils/ApiHelper";
import { getDate } from "~/common/utils/ApiHelper";

import { type ProductDataDTO } from "~/domains/product/types/ProductDataDTO";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { constructUrl, resolveApiUrl } from "~/utils/url/urlResolver";
import { getNextUID } from "~/common/utils/uid";
import { type PriceDataDTO } from "~/domains/pricing/types/PriceDataDTO";
import { type NetPriceData } from "~/domains/pricing/types/PriceData";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import dayjs from "dayjs";
import { apiPOST } from "~/app/api/apiCallsService";
import {
  type PaginatedFetchFuncProps,
  type PaginatedFetchFuncResponse,
} from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { type GroupedAccountsData } from "~/features/orders/common/DownloadOrderHistoryService/types";
import { type SDAccountDataDTO } from "~/features/orders/orderList/api/fetchOrderHistory/dto";
import { transformOrderAccount } from "~/features/orders/orderList/api/fetchOrderHistory/fetchOrderHistory";

interface ResponseItemDTO {
  preferences: string;
  ocsalesDocumentDataList: OrderConfirmationItemDTO[];
  nextSkipToken: string | undefined;
  pageCount: number;
  totalCount: number;
}

interface OrderConfirmationItemDTO {
  salesDocument: string;
  customerOrderId: string;
  products: ProductItemDTO[];
  account?: SDAccountDataDTO;
}

interface ProductItemDTO {
  product: ProductDataDTO;
  lineNumber: number;
  scheduledLineNumber: number | null;
  packSize: number;
  packCode: string;
  currentConfirmedDeliveryDate: string; // YYYY-MM-DD
  previousConfirmedDeliveryDate: string; // YYYY-MM-DD
  dateRequested: string; // YYYY-MM-DD
  currentConfirmedQuantity: number;
  previousConfirmedQuantity: number;
  requestedQuantity: number;
  stockReserved: number;
  status: string;
  primaryWarehouseName: string;
  netPrice: PriceDataDTO;
  priceUnit: string;
}

const baseUrl = "/abmonitor/list";

export interface OrderbookDataRequest {
  dateFrom?: Date;
  dateTo?: Date;
  account?: GroupedAccountsData;
}

export interface OrderbookDataResponse {
  orderbookData: OrderbookRowItem[];
  totalResults: number;
  pageCount: number;
}

export type FetchOrderbookDataProps =
  PaginatedFetchFuncProps<OrderbookDataRequest>;

export type FetchOrderbookDataResponse =
  PaginatedFetchFuncResponse<OrderbookDataResponse>;

export async function fetchOrderbookData({
  data,
  skipToken,
  signal,
}: FetchOrderbookDataProps): Promise<FetchOrderbookDataResponse> {
  const { dateFrom, dateTo, account } = data;
  const params = new URLSearchParams();
  if (typeof skipToken !== "undefined") {
    params.append("skiptoken", skipToken);
  }
  if (dateFrom) {
    params.append("startDate", dayjs(dateFrom).format("YYYY-MM-DD"));
  }
  if (dateTo) {
    params.append("endDate", dayjs(dateTo).format("YYYY-MM-DD"));
  }

  const apiUrl = resolveApiUrl(constructUrl(baseUrl, params));

  const response = await apiPOST(apiUrl, {
    body: account ? JSON.stringify(account) : undefined,
    headers: {
      "content-type": "application/json",
      CSRFToken: ApiHelper.getCsrfValue(),
    },
    signal,
  });

  const dto: ResponseItemDTO = await response.json();

  return {
    data: {
      orderbookData: dto.ocsalesDocumentDataList.map(transformLineItem).flat(),
      totalResults: dto.totalCount,
      pageCount: dto.pageCount,
    },
    nextSkipToken: dto.nextSkipToken,
  };
}

function transformLineItem(dto: OrderConfirmationItemDTO): OrderbookRowItem[] {
  let products: ProductItemDTO[] = dto.products || [];

  products = products.sort((a, b) =>
    a.lineNumber && b.lineNumber ? a.lineNumber - b.lineNumber : 0,
  );
  if (products.length === 1) {
    products[0].scheduledLineNumber = null;
  } else if (products.length > 0) {
    products.forEach((product, index) => {
      const nextProduct = products[index + 1];
      const prevProduct = products[index - 1];
      if (
        (nextProduct &&
          product.lineNumber !== nextProduct.lineNumber &&
          index === 0) ||
        (nextProduct &&
          product.lineNumber !== nextProduct.lineNumber &&
          index > 0 &&
          prevProduct &&
          product.lineNumber !== prevProduct.lineNumber) ||
        (prevProduct &&
          product.lineNumber !== prevProduct.lineNumber &&
          index === products.length - 1)
      ) {
        product.scheduledLineNumber = null;
      }
    });
  }

  return products.map((lineItemDto) => {
    const product = transformProduct(lineItemDto.product);
    const baseValue = lineItemDto.netPrice?.value ?? 0;
    const price: NetPriceData = {
      value: baseValue,
      currencyIso: lineItemDto.netPrice?.currencyIso ?? "XXX",
      unit: +(lineItemDto.priceUnit ?? 1),
      isSubscription: !product.quantityVisible,
      // TODO - AB Monitor does not supply surcharge data
      surcharge: 0,
      baseValue,
    };

    return {
      id: getNextUID(),
      orderLineNo:
        (lineItemDto.lineNumber && lineItemDto.scheduledLineNumber
          ? lineItemDto.lineNumber + ":" + lineItemDto.scheduledLineNumber
          : lineItemDto.lineNumber
          ? lineItemDto.lineNumber
          : "-") + "",
      customerOrderNo: dto.customerOrderId,
      schaefflerOrderNo: dto.salesDocument,
      product,
      isNew: lineItemDto.status === "1",
      isUpdated: lineItemDto.status === "2",
      curQuantity: lineItemDto.currentConfirmedQuantity,
      prevQuantity: lineItemDto.previousConfirmedQuantity,
      stockReserved: lineItemDto.stockReserved,
      totalQuantity: lineItemDto.requestedQuantity,
      curDeliveryDate: getDate(lineItemDto.currentConfirmedDeliveryDate),
      prevDeliveryDate: getDate(lineItemDto.previousConfirmedDeliveryDate),
      requestedDeliveryDate: getDate(lineItemDto.dateRequested),

      packCode: lineItemDto.packCode,
      packSize: lineItemDto.packSize,
      primaryWarehouseName: lineItemDto.primaryWarehouseName,
      netPrice: price,
      totalPrice: getTotalPrice(price, lineItemDto.currentConfirmedQuantity),
      account: transformOrderAccount(dto.account),
    };
  });
}
